import Head from "next/head";
import { useRouter } from "next/router";
import useSWRInfinite from "swr/infinite";

import { useCallback, useEffect, useMemo, useState } from "react";
import InfiniteScroll from "react-swr-infinite-scroll";

import camelCase from "lodash/camelCase";
import truncate from "lodash/truncate";
import uniqBy from "lodash/uniqBy";
import { Insight, LIST_FIELDS } from "models/insight";
import { twMerge } from "tailwind-merge";

import AlertBox from "components/AlertBox";
import { Loader } from "components/customElements/loader";
import { SKSpinner } from "components/customElements/skSpinner";
import DianomiAd from "components/DianomiAd";
import Dropdown from "components/Dropdown";
import EmptyState from "components/EmptyState";
import {
  InsightsListFilters,
  useInsightsFilters,
} from "components/InsightsListFilters";
import Paginator from "components/Paginator";
import PublicationSnippet from "components/PublicationSnippet";
import UndoMuteContent from "components/PublicationSnippet/UndoMuteContent";
import TextFilterInput from "components/TextFilterInput";
import TopPickSwitch from "components/TopPickSwitch";
import ViewModeToggle from "components/ViewModeToggle";
import { SK_CHANNEL } from "hooks/useActionCable";
import useAuthentication from "hooks/useAuthentication";
import { useChannel } from "hooks/useChannel";
import { useGlobalState } from "hooks/useGlobalState";
import useMediaQuery from "hooks/useMediaQuery";
import { ApiEndpoint, baseFetcher } from "utils/baseFetcher";
import { INSIGHT_TYPE_MAPPING, INSIGHT_TYPES } from "consts/insightTypes";

import DashboardPageSuggestedEntities from "../DashboardPageSuggestedEntities";
import DashboardPageSuggestedIP from "../DashboardPageSuggestedIP";

import styles from "./style.module.scss";

type DashboardPageInsightsListProps = {
  options?: {
    filter?: any;
    showFilters?: boolean;
    showTopPicksToggle?: boolean;
    showSorter?: boolean;
    showSearch?: boolean;
    showPresets?: boolean;
    showRefresh?: boolean;
    showMoreOptions?: boolean;
    hideViewModesToggle?: boolean;
    showSuggestedCard?: boolean;
    sort?: string;
    pageSize?: string;
  };
};

const DEFAULT_PAGE_SIZE = 12;

export const getSortOptions = (isMyReadingList: boolean) => {
  if (isMyReadingList) {
    return [
      { id: "suggested", name: "Suggested", sortStr: "suggested" },
      { id: "recent", name: "Recent", sortStr: "-published-at,-updated-at" },
    ];
  }

  return [
    { id: "recent", name: "Recent", sortStr: "-published-at,-updated-at" },
    { id: "trending", name: "Trending", sortStr: "trending" },
    {
      id: "viewed",
      name: "Viewed",
      sortStr: "-total-combined-read,-published-at",
    },
    {
      id: "discussed",
      name: "Discussed",
      sortStr: "-total-discussions,-published-at",
    },
    {
      id: "liked",
      name: "Liked",
      sortStr: "-likes-count",
    },
  ];
};

export const dashboardInsightsUrl = (
  pageIndex: number,
  filterTxt?: string,
  sortStr?: string,
  options?: any
) => {
  let url = new ApiEndpoint("/api/v2/insights");

  if (options?.filter?.collection === "bookmarked") {
    url = new ApiEndpoint(`/api/v2/locker-items`);
  } else if (options?.filter?.collection === "liked") {
    url = new ApiEndpoint(
      `/api/v2/accounts/${options?.accountId}/liked-insights`
    );
  } else if (
    options?.filter?.collection === "trending" ||
    sortStr === "trending"
  ) {
    url = new ApiEndpoint("/api/v2/trending-insights");
  } else if (sortStr === "suggested") {
    url = new ApiEndpoint("/api/v2/personalized-insights");
    url.searchParams.append("sort", "-published-at,-updated-at");
  } else {
    url.searchParams.append("sort", sortStr || "-published-at,-updated-at");
    url.searchParams.append("filter[aasm-state]", "published");
    if (options?.filter?.collection) {
      url.searchParams.append(
        "filter[collection]",
        options?.filter?.collection
      );
    } else {
      url.searchParams.append("filter[collection]", "all-insights");
    }
    if (options?.filter?.conviction) {
      url.searchParams.append(
        "filter[conviction]",
        options?.filter?.conviction
      );
    }
  }

  if (options?.filter?.unread) {
    url.searchParams.append("filter[unread]", "true");
  }
  if (options?.filter?.contentVerticalId) {
    url.searchParams.append(
      "filter[vertical-id]",
      options?.filter?.contentVerticalId
    );
  }
  if (options?.filter?.accountId) {
    url.searchParams.append("filter[account-id]", options?.filter?.accountId);
  }
  if (options?.filter?.sector) {
    url.searchParams.append("filter[sectors]", options?.filter?.sector);
  }

  url.searchParams.append(
    "page[size]",
    options?.pageSize || DEFAULT_PAGE_SIZE.toString()
  );
  url.searchParams.append("page[number]", `${pageIndex}`);

  if (options?.filter?.collection === "bookmarked") {
    url.searchParams.append("filter[is-owner]", "true");
    url.searchParams.append("filter[item-type]", "insight");
    url.searchParams.append("include", "account,asset,entities,my-shared-item");
    url.searchParams.append("sort", "-updated-at,-created-at");

    return url.href;
  }

  url.searchParams.append(
    "include",
    "content-vertical,primary-entity,account,company,locker-items,latest-comment-thread,latest-comment-thread.commenter,latest-comment-thread.attachment,smart-summary"
  );

  url.searchParams.append("fields[insights]", LIST_FIELDS.join(","));

  url.searchParams.append("fields[entities]", "slug,pretty-name");
  url.searchParams.append(
    "fields[accounts]",
    "slug,name,short-name,avatar-url,title,company-name,summary,areas-of-expertise,cannot-message-reason,is-corporate-officer,is-followed,is-insight-provider,activated"
  );
  url.searchParams.append(
    "fields[companies]",
    "slug,name,avatar-url,publish-under-company-name,title,analysts-count,summary,areas-of-expertise,is-followed"
  );
  url.searchParams.append("fields[content-verticals]", "slug,name");

  if (filterTxt) {
    url.searchParams.append("filter[fulltext]", filterTxt);
  }

  if (options?.selectedFilters?.insightTypes?.length) {
    INSIGHT_TYPES.filter((type) => !!type.filterKey).forEach((type) => {
      if (options?.selectedFilters?.insightTypes?.includes(type.key)) {
        url.searchParams.append(`filter[${type.filterKey}]`, "false");
      }
    });
    if (options?.selectedFilters?.insightTypes?.includes("isInsight")) {
      INSIGHT_TYPES.filter((type) => !!type.filterKey).forEach((type) => {
        if (!options?.selectedFilters?.insightTypes?.includes(type.key)) {
          url.searchParams.append(`filter[or][${type.filterKey}]`, "true");
        }
      });
    }
  }

  if (options?.selectedFilters?.geographies?.length) {
    url.searchParams.append(
      "filter[geographies]",
      options?.selectedFilters?.geographies
        ?.map?.((g: any) => g.name)
        ?.join(",")
    );
  }
  if (options?.selectedFilters?.countries?.length) {
    url.searchParams.append(
      "filter[countries]",
      options?.selectedFilters?.countries?.map?.((c: any) => c.name)?.join(",")
    );
  }
  if (options?.selectedFilters?.sectors?.length) {
    url.searchParams.append(
      "filter[sectors]",
      options?.selectedFilters?.sectors?.map?.((s: any) => s.name)?.join(",")
    );
  }
  if (options?.selectedFilters?.verticals?.length) {
    url.searchParams.append(
      "filter[vertical-ids]",
      options?.selectedFilters?.verticals?.map?.((v: any) => v.id)?.join(",")
    );
  }
  if (options?.selectedFilters?.imperatives?.length) {
    url.searchParams.append(
      "filter[imperative]",
      options?.selectedFilters?.imperatives?.join(",")
    );
  }
  if (options?.selectedFilters?.timeHorizons?.length) {
    url.searchParams.append(
      "filter[time-horizon]",
      options?.selectedFilters?.timeHorizons?.join(",")
    );
  }
  if (
    options?.selectedFilters?.publishedDates?.from ||
    options?.selectedFilters?.publishedDates?.to
  ) {
    url.searchParams.append(
      "filter[time-scope]",
      [
        options?.selectedFilters?.publishedDates?.from ||
          "2014-08-31T17:00:00.000Z",
        options?.selectedFilters?.publishedDates?.to,
      ]
        .filter((t) => t)
        .join(",")
    );
  }
  if (options?.topPicksOnly) {
    url.searchParams.append("filter[conviction]", "1");
  }

  return url.href;
};

const DashboardPageInsightsList = ({
  options,
}: DashboardPageInsightsListProps) => {
  const {
    account: loggedInAccount,
    authenticated,
    accountId,
    interfaceSetting,
    updateInterfaceSetting,
  } = useAuthentication();

  const { mobile } = useMediaQuery();

  const router = useRouter();
  const collection = router.query.collection as string;
  const sort = useMemo(
    () =>
      (router.query.sort as string) ||
      interfaceSetting?.insightsFiltersSort?.[camelCase(collection)],
    [collection, interfaceSetting?.insightsFiltersSort, router.query.sort]
  );
  const queryPage = router.query.page && parseInt(router.query.page as string);

  useEffect(() => {
    if (!collection) return;

    if (
      !router.query.sort &&
      interfaceSetting?.insightsFiltersSort?.[camelCase(collection)]
    ) {
      router.push(
        {
          pathname: router.pathname,
          query: {
            ...router.query,
            sort,
          },
        },
        undefined,
        { shallow: true }
      );
    }
  }, [
    collection,
    interfaceSetting?.insightsFiltersSort,
    router,
    router.query.sort,
    sort,
  ]);

  const isMyReadingList = options?.filter?.collection === "my-reading-list";

  const sortOptions = useMemo(
    () => getSortOptions(isMyReadingList),
    [isMyReadingList]
  );

  const [filterTxt, setFilterTxt] = useState("");
  const [topPicksOnly, setTopPicksOnly] = useState(false);
  const [isCompact, setCompactView] = useState(mobile);
  const sortOption = useMemo(
    () => sortOptions.find((o) => o.id === sort) || sortOptions[0],
    [sort, sortOptions]
  );

  const { InsightsSelectedFilters, filtersProps, selectedFilters } =
    useInsightsFilters({ showPresets: options?.showPresets });

  const swr = useSWRInfinite(
    (pageIndex, previousPageData) => {
      if (previousPageData && !previousPageData?.data?.length) return null;
      return dashboardInsightsUrl(
        queryPage || pageIndex + 1,
        filterTxt,
        options?.sort || sortOption.sortStr,
        {
          ...options,
          selectedFilters,
          topPicksOnly,
          pageSize: authenticated ? "12" : "5",
          accountId,
        }
      );
    },
    baseFetcher,
    {
      revalidateOnFocus: false,
    }
  );
  const { data, isValidating, mutate, size: page, setSize: setPage } = swr;

  const totalPages = data?.[page - 1]?.meta?.totalPages;
  const hasMore = !queryPage && totalPages > page;
  const isEmpty = !data?.[0]?.data?.length;
  const isRefreshing = isValidating && data && data.length === page;

  useEffect(() => {
    if (page && totalPages && page > totalPages) {
      router.replace({
        pathname: router.pathname,
        query: {
          ...router.query,
          page: totalPages,
        },
      });
    }
  }, [page, router, totalPages]);

  const description = useMemo(() => {
    const firstPublication = data?.[0]?.data?.[0];
    if (collection === "all-insights") {
      return truncate(
        `Browse the latest Insights on Smartkarma.${
          firstPublication
            ? ` Latest Insight - ${firstPublication?.tagline}`
            : "."
        }`,
        {
          length: 155,
          separator: /[/\- ]/,
        }
      );
    }

    if (collection === "trending") {
      return truncate(
        `See what's Trending on Smartkarma.${
          firstPublication
            ? ` Top Trending - ${firstPublication?.tagline}`
            : "."
        }`,
        {
          length: 155,
          separator: /[/\- ]/,
        }
      );
    }

    return "";
  }, [collection, data]);

  const onHide = useCallback(
    async (
      type: string,
      publication: any,
      hideRest = true,
      resetFilter = false
    ) => {
      await mutate(() => data, {
        populateCache: (_, pages) => {
          return (
            pages?.map((page) => {
              return {
                ...page,
                data: page.data?.map((item: any) => {
                  if (type === "account" || type === "company") {
                    const authorRoot =
                      type === "account"
                        ? publication.account
                        : publication.company;
                    const author =
                      type === "account" ? item.account : item.company;
                    if (publication.id === item.id) {
                      if (hideRest && !resetFilter) {
                        return item;
                      } else if (!hideRest && !resetFilter) {
                        item.isHidden = true;
                        return { ...item };
                      }
                    }
                    if (author?.id === authorRoot?.id && resetFilter) {
                      item.isHidden = false;
                    } else if (author?.id === authorRoot?.id && !resetFilter) {
                      item.isHidden = true;
                    }
                    return { ...item };
                  }
                  if (type === "entity") {
                    const entity = publication.primaryEntity;
                    if (publication.id === item.id) {
                      if (hideRest && !resetFilter) {
                        return item;
                      } else {
                        item.isHidden = true;
                        return { ...item };
                      }
                    }
                    if (entity?.id === item.primaryEntity?.id && resetFilter) {
                      item.isHidden = false;
                    } else if (
                      entity?.id === item.primaryEntity?.id &&
                      !resetFilter
                    ) {
                      item.isHidden = true;
                    }
                    return { ...item };
                  }

                  const insightType = INSIGHT_TYPE_MAPPING.find(
                    (i) => i.mutedContentsKey === type
                  );
                  if (insightType?.key) {
                    if (publication.id === item.id) {
                      if (hideRest) {
                        return item;
                      } else {
                        item.isHidden = true;
                        return { ...item };
                      }
                    }

                    if (item[insightType.key] && resetFilter) {
                      item.isHidden = false;
                    } else if (item[insightType.key] && !resetFilter) {
                      item.isHidden = true;
                    }
                    return { ...item };
                  }
                  return item;
                }),
              };
            }) || []
          );
        },
        revalidate: false,
      });
    },
    [data, mutate]
  );

  const onShowMuteBanner = useCallback(
    async (showContent: any, publication: any) => {
      await mutate(() => data, {
        populateCache: (_, pages) => {
          return (
            pages?.map((page) => {
              return {
                ...page,
                data: page.data?.map((item: any) => {
                  if (item.id === publication.id) {
                    if (showContent) {
                      return { ...item, showContent };
                    } else {
                      return { ...item, showContent: undefined };
                    }
                  }
                  return item;
                }),
              };
            }) || []
          );
        },
        revalidate: false,
      });
    },
    [data, mutate]
  );

  const onSelect = useCallback(
    (selected) => {
      updateInterfaceSetting({
        insightsFiltersSort: {
          ...interfaceSetting?.insightsFiltersSort,
          [camelCase(collection)]: selected.id,
        },
      });
    },
    [collection, interfaceSetting?.insightsFiltersSort, updateInterfaceSetting]
  );

  const [_showFeedSettingsModal, setShowFeedSettings] = useGlobalState(
    "showFeedSettingsModal",
    false
  );

  const [refreshingReadinglist, setRefreshingReadinglist] = useGlobalState(
    "refreshingReadinglist",
    false
  );

  useChannel({
    key: "DashboardPageInsightList",
    name: `private-account-${accountId}`,
    channel: {
      channel: SK_CHANNEL,
      room: `private-account-${accountId}`,
    },
    onReceived: async (message: any) => {
      const { event } = message;
      console.log(message);
      switch (event) {
        case "reading-list-creating":
        case "reading-list-updating":
          if (isMyReadingList) {
            // setRefreshingReadinglist(true);
          }
          break;
        case "reading-list-created":
        case "reading-list-updated":
          if (isMyReadingList) {
            await mutate();
            // setRefreshingReadinglist(false);
          }
          break;
      }
    },
  });

  const isFeedEmpty = useMemo(() => {
    return isMyReadingList && isEmpty && !isValidating && !filterTxt;
  }, [filterTxt, isEmpty, isMyReadingList, isValidating]);

  const publications = useMemo(() => {
    const items =
      swr.data
        ?.reduce((acc, page) => {
          if (page?.data) return [...acc, ...page.data];
          return acc;
        }, [])
        ?.filter((i: any) => !i.isHidden) || [];

    return uniqBy(items, (publication: Insight) => publication.id);
  }, [swr.data]);

  const snippetStyle = !mobile ? "snippetV2" : "snippet";

  return (
    <>
      <Head>
        <meta name="description" content={description} />
        <meta property="og:description" content={description} />
      </Head>
      <div
        className={twMerge(
          styles.container,
          "relative min-h-[max(calc(100vh-210px),720px)]"
        )}
      >
        {loggedInAccount?.isPreviewPass &&
          isMyReadingList &&
          (refreshingReadinglist || (isEmpty && !isValidating)) && (
            <div className={twMerge("flex flex-col", "p-[15px] md:p-[30px]")}>
              {isEmpty && !refreshingReadinglist && (
                <AlertBox
                  className={twMerge(
                    "flex-1",
                    "p-[15px] md:p-[30px]",
                    "rounded-lg"
                  )}
                >
                  <div className={twMerge("text-center")}>
                    <p>
                      There isn&apos;t any insight matching your feed settings.
                    </p>
                    <p>
                      Please update your{" "}
                      <a
                        className="text-smart-teal cursor-pointer"
                        onClick={() => setShowFeedSettings(true)}
                      >
                        feed settings
                      </a>
                    </p>
                  </div>
                </AlertBox>
              )}
              {refreshingReadinglist && (
                <div className="flex justify-center mt-[30px]">
                  <SKSpinner className="!text-active-black">
                    Refreshing My Feed
                  </SKSpinner>
                </div>
              )}
            </div>
          )}
        {options?.showFilters && (
          <div
            className={twMerge(
              "top-[-51px] md:top-[-45px] left-0 right-0 flex static md:absolute"
            )}
          >
            <InsightsListFilters
              {...filtersProps}
              showMoreFilters
              showTopCountries
              showPresets={options?.showPresets}
            />
          </div>
        )}
        <InsightsSelectedFilters />
        {!mobile && (
          <>
            {(!isFeedEmpty || !loggedInAccount?.isPreviewPass) && (
              <div className={styles.filter}>
                {options?.showRefresh && (
                  <span
                    className={styles.refresh}
                    onClick={() => {
                      mutate();
                    }}
                  >
                    Refresh
                  </span>
                )}
                <div className="flex-1">
                  {options?.showSearch && (
                    <TextFilterInput
                      value={filterTxt}
                      onChange={setFilterTxt}
                    />
                  )}
                </div>
                {options?.showTopPicksToggle && (
                  <div className="mr-[10px]">
                    <TopPickSwitch
                      checked={topPicksOnly}
                      onSwitch={setTopPicksOnly}
                    />
                  </div>
                )}
                {options?.showSorter && (
                  <Dropdown
                    options={sortOptions}
                    onSelect={onSelect}
                    queryParam="sort"
                  />
                )}
                {!options?.hideViewModesToggle && (
                  <ViewModeToggle
                    onToggle={setCompactView}
                    isCompact={isCompact}
                    showPager
                    setSize={setPage}
                  />
                )}
              </div>
            )}
          </>
        )}
        <div className={styles.list}>
          <InfiniteScroll
            swr={{ data, mutate, isValidating, size: page, setSize: setPage }}
            isReachingEnd={!hasMore}
            endingIndicator={
              isValidating ? (
                <Loader />
              ) : (
                !isFeedEmpty &&
                (isEmpty ? (
                  filterTxt ? (
                    <EmptyState
                      type="search"
                      query={filterTxt}
                      onClearClick={() => setFilterTxt("")}
                    />
                  ) : (
                    <EmptyState type="publication" />
                  )
                ) : queryPage ? (
                  <Paginator totalPages={totalPages} />
                ) : isMyReadingList ? (
                  <div className="pt-[45px]">
                    <h4 className="pl-[15px] md:pl-[30px]">
                      Trending Insights
                    </h4>
                    <DashboardPageInsightsList
                      options={{
                        filter: { collection: "trending" },
                        hideViewModesToggle: true,
                      }}
                    />
                  </div>
                ) : (
                  <div className={styles.endOfList}>No more insights</div>
                ))
              )
            }
          >
            <>
              {publications?.map?.((publication: any, index: number) => {
                // TODO: continue revamp
                const moreOptions = options?.showMoreOptions
                  ? {
                      onContentRemovedCallback: (type: any, undo = false) =>
                        onHide(type, publication, true, undo),
                      onCloseContentRemoved: (type: any) =>
                        onHide(type, publication, false),
                      setShowContentRemoved: (id: any) =>
                        onShowMuteBanner(id, publication),
                    }
                  : undefined;

                if (publication.showContent?.show) {
                  const { id, showContent } = publication;

                  return (
                    <div
                      key={publication.id}
                      className={twMerge(
                        "mx-[30px]",
                        "py-[20px]",
                        "border-0 border-b border-solid border-inactive-gray"
                      )}
                    >
                      <UndoMuteContent
                        title={showContent?.title}
                        type={showContent?.type}
                        id={showContent?.id}
                        onUndo={() => {
                          onShowMuteBanner(id, publication);
                          onHide(showContent?.type, publication, true, true);
                        }}
                        onClose={() =>
                          onHide(showContent?.type, publication, false)
                        }
                      />
                    </div>
                  );
                }

                if (index === 8 && options?.showSuggestedCard && mobile) {
                  return (
                    <div key={publication.id} className="">
                      <DashboardPageSuggestedEntities />
                      <DashboardPageSuggestedIP />
                      <PublicationSnippet
                        publication={publication}
                        isCompact={isCompact}
                        useModal={authenticated}
                        moreOptions={moreOptions}
                        swr={swr}
                        style={snippetStyle}
                      />
                    </div>
                  );
                }

                if (index === 1) {
                  return (
                    <div key={publication.id} className="">
                      <PublicationSnippet
                        key={publication.id}
                        publication={publication}
                        isCompact={isCompact}
                        useModal={authenticated}
                        moreOptions={moreOptions}
                        swr={swr}
                        style={snippetStyle}
                      />
                      <DianomiAd type="in_list_medium" />
                    </div>
                  );
                }

                if (index === 4) {
                  return (
                    <div key={publication.id} className="">
                      <PublicationSnippet
                        key={publication.id}
                        publication={publication}
                        isCompact={isCompact}
                        useModal={authenticated}
                        moreOptions={moreOptions}
                        swr={swr}
                        style={snippetStyle}
                      />
                      <DianomiAd type="in_list" />
                    </div>
                  );
                }

                return (
                  <PublicationSnippet
                    key={publication.id}
                    publication={publication}
                    isCompact={isCompact}
                    useModal={authenticated}
                    moreOptions={moreOptions}
                    swr={swr}
                    style={snippetStyle}
                  />
                );
              })}
            </>
          </InfiniteScroll>
        </div>
        {isFeedEmpty && (
          <div
            className={twMerge(!loggedInAccount?.isPreviewPass && "pt-[45px]")}
          >
            <h4 className="pl-[15px] md:pl-[30px]">Trending Insights</h4>
            <DashboardPageInsightsList
              options={{
                filter: { collection: "trending" },
                hideViewModesToggle: true,
              }}
            />
          </div>
        )}
      </div>
    </>
  );
};

export default DashboardPageInsightsList;
